import React from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { useAuthenticator } from '@aws-amplify/ui-react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  Container,
  Chip,
  IconButton,
  Tooltip,
} from '@mui/material';
import LogoutIcon from '@mui/icons-material/Logout';
import { detectShift, SHIFT_LABELS, ShiftType } from './types/shift';
import ErrorBoundary from './components/ErrorBoundary';
import pkg from '../package.json';

const SHIFT_CHIP_COLORS: Record<ShiftType, string> = {
  NIGHT: '#5c6bc0',
  DAY: '#ffa726',
  EVENING: '#ab47bc',
};

const App: React.FC = () => {
  const { signOut, user } = useAuthenticator();
  const location = useLocation();
  const currentShift = detectShift();

  const navItems = [
    { path: '/history', label: 'History' },
    { path: '/dashboard', label: 'Dashboard' },
    { path: '/calendar', label: 'Calendar' },
  ];

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          bgcolor: '#0f172a',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Toolbar sx={{ gap: 1 }}>
          {/* Logo — canonical link to Handoff Form */}
          <Box
            component={Link}
            to="/"
            aria-label="Handoff Form — home"
            sx={{
              textDecoration: 'none',
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              mr: 2,
              borderRadius: '6px',
              px: 0.5,
              py: 0.25,
              '&:hover': { bgcolor: 'rgba(0, 188, 212, 0.06)' },
              '&:focus-visible': { outline: '2px solid #00bcd4', outlineOffset: 2 },
            }}
          >
            <Box
              component="img"
              src="/nocland.png"
              alt="NOCLAND"
              sx={{
                height: 32,
                width: 'auto',
              }}
            />
            <Typography
              variant="subtitle1"
              sx={{
                color: 'text.primary',
                fontWeight: 700,
                display: { xs: 'none', sm: 'block' },
              }}
            >
              Shift Summary
            </Typography>
            <Typography
              sx={{
                color: '#475569',
                fontSize: '0.6rem',
                fontWeight: 500,
                display: { xs: 'none', sm: 'block' },
              }}
            >
              v{pkg.version}
            </Typography>
          </Box>

          {/* Current Shift Badge */}
          <Chip
            label={SHIFT_LABELS[currentShift]}
            size="small"
            sx={{
              bgcolor: SHIFT_CHIP_COLORS[currentShift],
              color: 'white',
              fontWeight: 600,
              fontSize: '0.7rem',
              height: 24,
              mr: 1,
            }}
          />

          {/* Nav Links */}
          <Box sx={{ flexGrow: 1, display: 'flex', gap: 0.5 }}>
            {navItems.map((item) => {
              const isActive = location.pathname === item.path;
              return (
                <Button
                  key={item.path}
                  component={Link}
                  to={item.path}
                  size="small"
                  sx={{
                    color: isActive ? 'primary.main' : 'text.secondary',
                    fontWeight: isActive ? 700 : 400,
                    fontSize: '0.85rem',
                    borderRadius: '8px',
                    px: 1.5,
                    bgcolor: isActive ? 'rgba(0, 188, 212, 0.08)' : 'transparent',
                    '&:hover': {
                      bgcolor: 'rgba(0, 188, 212, 0.12)',
                      color: 'primary.main',
                    },
                  }}
                >
                  {item.label}
                </Button>
              );
            })}
          </Box>

          {/* User */}
          <Typography
            variant="body2"
            sx={{ color: 'text.secondary', fontSize: '0.8rem', mr: 1 }}
          >
            {user?.signInDetails?.loginId ?? ''}
          </Typography>
          <Tooltip title="Sign out">
            <IconButton
              onClick={signOut}
              size="small"
              sx={{ color: 'text.secondary', '&:hover': { color: 'error.main' } }}
            >
              <LogoutIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ py: 4 }}>
        <ErrorBoundary>
          <Outlet />
        </ErrorBoundary>
      </Container>
    </Box>
  );
};

export default App;
